import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, ArrowRight, Crown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const { language } = useLanguage();
  const { refreshUser } = useAuth();
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    const checkPaymentStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(
          `${API_URL}/api/checkout/status/${sessionId}`,
          { headers }
        );
        
        const data = response.data;
        setPaymentInfo(data);
        
        if (data.payment_status === 'paid' || data.status === 'completed') {
          setStatus('success');
          // Refresh user context to get updated plan
          if (refreshUser) {
            await refreshUser();
          }
        } else if (data.status === 'expired') {
          setStatus('failed');
        } else if (pollCount < 10) {
          // Keep polling
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };
    
    checkPaymentStatus();
  }, [sessionId, pollCount, refreshUser]);
  
  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-accent animate-spin" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'Betaling wordt verwerkt...' : 'Processing payment...'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {language === 'nl' 
                ? 'Even geduld, we controleren uw betaling.'
                : 'Please wait while we verify your payment.'}
            </p>
          </div>
        );
        
      case 'success':
        return (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-10 h-10 text-success" />
            </motion.div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'Betaling geslaagd!' : 'Payment successful!'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {paymentInfo?.message || (language === 'nl' 
                ? 'Welkom bij KenmerkCheck Pro! U heeft nu toegang tot alle premium functies.'
                : 'Welcome to KenmerkCheck Pro! You now have access to all premium features.')}
            </p>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-900/30 dark:to-amber-800/30 text-amber-700 dark:text-amber-300 text-sm font-medium mb-8">
              <Crown className="w-4 h-4" />
              {paymentInfo?.plan === 'team' ? 'Team' : 'Pro'} Account
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/bulk"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors"
              >
                {language === 'nl' ? 'Bulk decoderen' : 'Bulk decode'}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/bank"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                {language === 'nl' ? 'Bank upload' : 'Bank upload'}
              </Link>
            </div>
          </div>
        );
        
      case 'failed':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-error" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'Betaling niet voltooid' : 'Payment not completed'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {language === 'nl' 
                ? 'De betaling is niet gelukt of geannuleerd. Probeer het opnieuw.'
                : 'The payment was not successful or was cancelled. Please try again.'}
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors"
            >
              {language === 'nl' ? 'Terug naar prijzen' : 'Back to pricing'}
            </Link>
          </div>
        );
        
      case 'error':
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-slate-400" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'Er ging iets mis' : 'Something went wrong'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {language === 'nl' 
                ? 'We konden de betalingsstatus niet ophalen. Neem contact op als het probleem aanhoudt.'
                : 'We could not retrieve the payment status. Please contact us if the problem persists.'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors"
            >
              {language === 'nl' ? 'Naar home' : 'Go to home'}
            </Link>
          </div>
        );
    }
  };
  
  return (
    <div className="min-h-[60vh] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 shadow-lg">
          {renderContent()}
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
